import { useState, useMemo } from "react";
import styled from "styled-components";
import { useInfiniteQuery } from '@tanstack/react-query';
import { AnimatePresence } from "framer-motion";
import { useParams } from "react-router-dom";

import Button from "@/components/Button";
import { ReactComponent as PlusIcon } from "@/assets/icons/plus.svg";
import StrategyChangelogCard from "./StrategyChangelogCard";
import StrategyFormModal from "./StrategyFormModal";
import { TableHeader } from "@/components/Table";
import { SectionHeading } from "@/components/Layout/Parts";
import { ChangelogResource, PaginatedResponse } from "@/types/resources";
import { api } from "@/utils/api";
import useChannelsFilter from "@/hooks/useChannelsFilter";
import useDidUpdate from "@/hooks/useDidUpdate";
import ListWrap from "./ListWrap";
import ExportButton from "@/components/ExportButton";
import HideElements from "@/components/HideElements";
import InfiniteLoader from "@/components/InfiniteLoader";

const StyledSectionHeading = styled(SectionHeading)`
  margin: 0;
  flex: 1;
`;

const StyledButton = styled(Button)`
  margin-left: 8px;
`;

const StrategyChangelogList = () => {
  const [showFormModal, setShowFormModal] = useState(false);

  const { teamId } = useParams();

  const [filteredChannelsIds, filteredChannelsKey] = useChannelsFilter();

  const fetchChangelogs = ({ pageParam = `/v1/changelogs` }) => {
    return api.get<never, PaginatedResponse<ChangelogResource>>(pageParam, {
      params: {
        ...(teamId !== "all" && {
          "filter[team_id]": teamId,
        }),
        ...(filteredChannelsIds?.length && {
          "filter[channel_id]": filteredChannelsIds.join(","),
        }),
      },
    });
  };

  const changelogsQueryKey = ["strategy_changelog", { teamId }];

  const {
    data,
    isError,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    refetch,
    remove,
    fetchNextPage,
  } = useInfiniteQuery<PaginatedResponse<ChangelogResource>>(
    changelogsQueryKey,
    fetchChangelogs,
    {
      getNextPageParam: (lastPage) => {
        return lastPage.links.next;
      },
    }
  );

  const changelogs = useMemo(
    () =>
      Object.keys(data?.pages || {}).reduce<ChangelogResource[]>(
        (acc, currKey: any) => {
          return [...acc, ...data!.pages[currKey].data];
        },
        []
      ),
    [data]
  );

  useDidUpdate(() => {
    remove();
    refetch();
  }, [filteredChannelsKey]);

  const fetchCsv = () =>
    api.get<never, string>(`/v1/changelogs/export`, {
      params: {
        ...(teamId !== "all" && { "filter[team_id]": teamId }),
        ...(filteredChannelsIds?.length && {
          "filter[channel_id]": filteredChannelsIds.join(","),
        }),
      },
    });

  return (
    <>
      <TableHeader>
        <StyledSectionHeading>Recent events</StyledSectionHeading>
        <HideElements>
          <ExportButton fileName="changelog.csv" fetchCsv={fetchCsv} />
        </HideElements>
        {teamId !== "all" && (
          <StyledButton
            small
            color="action"
            onClick={() => setShowFormModal(true)}
          >
            <PlusIcon /> Add event
          </StyledButton>
        )}
      </TableHeader>
      <ListWrap
        isLoading={isLoading}
        isError={isError}
        hasData={!!changelogs?.length}
      >
        {changelogs.map((changelog) => (
          <StrategyChangelogCard
            key={changelog.id}
            changelog={changelog}
            queryKey={changelogsQueryKey}
          />
        ))}
      </ListWrap>
      <InfiniteLoader
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
        isFetchingNextPage={isFetchingNextPage}
      />
      {teamId !== "all" && (
        <AnimatePresence>
          {showFormModal && (
            <StrategyFormModal
              onClose={() => setShowFormModal(false)}
              queryKey={changelogsQueryKey}
            />
          )}
        </AnimatePresence>
      )}
    </>
  );
};

export default StrategyChangelogList;
